import { useCallback } from "react";
import useRecorderContext from "./useRecorderContext";
import logger from "../utils/logger";
import { onboardingService } from "../services/onboardingService";

type PresetFn = (
  width: number,
  height: number,
  screenAspectRatio: number,
  roundedWebcam: boolean,
  canvasRatio: string,
  edgeToEdge?: boolean
) => any[];

export default function useLayoutManager() {
  const {
    setInitialStreamLayout,
    setLayoutVersion,
    roundedWebcam,
    stageRef,
    setHasManualModifications,
    edgeToEdge,
  } = useRecorderContext();

  /**
   * Apply a layout preset based on canvas size and screen aspect ratio
   */
  const applyPreset = useCallback(
    (
      preset: PresetFn,
      screenAspectRatio: number,
      width: number,
      height: number,
      canvasRatio: string,
      roundedWebcamParam?: boolean,
      preserveManualModifications = false,
      edgeToEdgeOverride?: boolean,
      isUserInitiated = false
    ) => {
      // Debug: Check if this is WebcamFull preset
      const isWebcamFull =
        preset.name?.includes("WebcamFull") ||
        preset.toString().includes("WebcamFull");

      // Debug logging for all preset applications
      const presetName = preset.name || "Unknown";

      if (isWebcamFull) {
        const stackTrace = new Error().stack;
        // logger.info("🔵 WebcamFull preset applied:", {
        //   screenAspectRatio,
        //   width,
        //   height,
        //   roundedWebcam: roundedWebcamParam ?? roundedWebcam,
        //   preserveManualModifications,
        //   manualModificationsPreserved: preserveManualModifications,
        //   timestamp: new Date().toISOString(),
        //   stackTrace: stackTrace
        //     ? stackTrace.split("\n").slice(2, 6).join("\n")
        //     : "No stack trace",
        // });
      }

      const layout = preset(
        width,
        height,
        screenAspectRatio,
        roundedWebcamParam ?? roundedWebcam,
        canvasRatio,
        edgeToEdgeOverride ?? edgeToEdge
      );
      
      
      // logger.info("📐 Layout generated by preset:", {
      //   presetName,
      //   layoutLength: layout.length,
      //   layout: layout.map(item => ({
      //     stream: item.stream,
      //     x: item.x,
      //     y: item.y,
      //     width: item.width,
      //     height: item.height
      //   }))
      // });
      
      // Deduplicate layout by stream type to prevent duplicate webcam/screen objects
      const seenStreams = new Set<string>();
      const deduplicatedLayout = layout.filter((item) => {
        if (seenStreams.has(item.stream)) {
          logger.warn(`🚨 Duplicate stream detected and removed: ${item.stream}`);
          return false;
        }
        seenStreams.add(item.stream);
        return true;
      });
      
      const newLayout = deduplicatedLayout.map((item) => ({ ...item })); // force new references
      setInitialStreamLayout(newLayout);
      setLayoutVersion((v) => v + 1);

      // logger.info("✅ Layout applied successfully:", {
      //   presetName,
      //   layoutVersion: "incremented",
      //   preserveManualModifications
      // });

      // Only reset manual modifications if explicitly requested (for user-initiated preset changes)
      if (!preserveManualModifications) {
        setHasManualModifications(false);
      }
      
      if (isUserInitiated) {
        onboardingService.markTryLayouts().catch(() => {});
      }
      const canvas = stageRef?.current?.getCanvas();
      canvas?.requestRenderAll();
    },
    [
      setInitialStreamLayout,
      setLayoutVersion,
      roundedWebcam,
      stageRef,
      setHasManualModifications,
      edgeToEdge,
    ]
  );

  /**
   * Reset manual modifications to allow preset application
   */
  const resetManualModifications = useCallback(() => {
    // This will be used by components to reset their manual modification state
    // when a preset is intentionally applied
    setLayoutVersion((v) => v + 1);
    setHasManualModifications(false);
  }, [setLayoutVersion, setHasManualModifications]);

  return {
    applyPreset,
    resetManualModifications,
  };
}
